import { APP_INITIALIZER, inject, Injector, NgModule, PLATFORM_ID, provideZoneChangeDetection, TransferState } from '@angular/core';
import { BrowserModule, provideClientHydration } from '@angular/platform-browser';
import { isPlatformBrowser, Location, LOCATION_INITIALIZED, registerLocaleData } from '@angular/common';
import { Router, UrlSerializer } from '@angular/router';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { finalize } from 'rxjs/operators';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ToTopButtonModule } from './ui-components/to-top-button/to-top-button.module';
import { ApiInterceptor } from './api.interceptor';
import { ApiService } from './pages/sign-up/services/api.service';
import { ContactUsModalModule } from './modals/contact-us-modal/contact-us-modal.module';
import { RequestDemoModalModule } from './modals/request-demo-modal/request-demo-modal.module';
import { TitleService } from "./services/title.service";
import { LocaleService } from "./locale/locale.service";
import { TranslateLoader, TranslateModule, TranslateService } from "@ngx-translate/core";
import { translateBrowserLoaderFactory } from "./services/translate-browser.loader";
import { ContactSupportModalModule } from "./modals/contact-support-modal/contact-support-modal.module";
import { ToasterModule } from "./ui-components/toaster/toaster.module";
import { TRANSLATE_HTTP_LOADER_CONFIG } from '@ngx-translate/http-loader';
import { BUILD_CONFIG } from 'core/static/build-config.token';
import { environment } from 'src/environments/environment';
import { provideAnimations } from '@angular/platform-browser/animations';
import { AppIconsLibrary } from 'core/components/icons/icons-library.service';
import { iconAlert } from 'main/icons/icon-alert';
import { iconEyeSlash } from 'main/icons/icon-eye-slash';
import { iconEye } from 'main/icons/icon-eye';
import { iconClose } from 'main/icons/icon-close';
import { iconCheckmark } from 'main/icons/icon-checkmark';
import { iconCalendar } from 'main/icons/icon-calendar';
import { iconArrowRight } from 'main/icons/icon-arrow-right';
import { iconPlus } from 'main/icons/icon-plus';
import { iconSearch } from 'main/icons/icon-search';
import { iconCheckRound } from 'main/icons/icon-check-round';
import { iconCheckboxEmpty } from 'main/icons/icon-checkbox-empty';
import { iconCheckFrameActive } from 'main/icons/icon-check-frame-active';
import { iconCheckbox } from 'main/icons/icon-checkbox';
import { iconRboxEmpty } from 'main/icons/icon-rbox-empty';
import { iconRboxFilled } from 'main/icons/icon-rbox-filled';
import { iconAngleDown } from 'main/icons/icon-angle-down';
import { MediaQuery } from 'core/services/media-query.service';
import { iconRadiobutton } from 'main/icons/icon-radiobutton';
import { iconRadiobuttonMark } from 'main/icons/icon-radiobutton-mark';
import { iconMoveArrows } from 'main/icons/icon-move-arrows';
import { HeaderComponent } from 'main/components/header/header.component';
import { FooterComponent } from 'main/components/footer/footer.component';
import { RouterBehavior } from 'core/utils/router';
import { ScrollRestoration } from 'core/utils/scroll-restoration.service';
import { ModalComponent } from 'core/modal/components/modal.component';
import localeDe from '@angular/common/locales/de';
import { iconCheckBold } from 'main/icons/icon-check-bold';
import { getSegmentFromUrl, I18nUrlSerializer } from './i18n.serializer';
import { AppService } from './app.service';
import { AllowedLangsEnum } from './locale/locale.enum';
import { forkJoin } from 'rxjs';
import { IconsModule } from "core/components/icons/icons.module";
import { MediaIfDirective } from 'core/directives/media-if.directive';
import { PromoLineComponent } from './_main/components/promo-line/promo-line.component';

registerLocaleData(localeDe);

export function appLanguageInitializerFactory(translate: TranslateService, injector: Injector, location: Location, app: AppService) {
  return () => new Promise<any>((resolve: any) => {
    const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
    const pathLang = getSegmentFromUrl(location.path(), 'lang');
    const lang = pathLang || app.currentLangIso || (typeof navigator === 'undefined' ? 'de' : (navigator.language || 'de').split('-')[0]);
    const langToSet = Object.keys(AllowedLangsEnum).map(k => k.toLowerCase()).includes(lang) ? lang as AllowedLangsEnum : AllowedLangsEnum.EN;
    app.currentLangIso = langToSet;
    locationInitialized.then(() => {
      translate.setFallbackLang('en');
      translate.use(langToSet).pipe(finalize(() => {
        resolve(null);
      })).subscribe();
    });
  });
}

export function appInitializerFactory(locale: LocaleService, app: AppService) {
  const platformId = inject(PLATFORM_ID);
  return () => {
    if (!isPlatformBrowser(platformId)) {
      return new Promise<void>((resolve) => {
        return forkJoin([app.getPromocodes()]).subscribe(() => resolve());
      });
    }
    return new Promise<void>((resolve) => {
      locale.getLocationInfo().subscribe();
      return forkJoin([app.getPromocodes()]).subscribe(() => resolve());
    });
  };
}

const build: BaseBuildConfig = {
  appName: '',
  client: '',
  buildTime: '',
  ssr: false,
  app: true,
  production: environment.production,
};

@NgModule({
  declarations: [
    AppComponent
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    AppRoutingModule,
    HeaderComponent,
    FooterComponent,
    PromoLineComponent,
    ToTopButtonModule,
    MediaIfDirective,
    ModalComponent,
    ToasterModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translateBrowserLoaderFactory,
        deps: [HttpClient, TransferState],
      }
    }),
    IconsModule
  ],
  providers: [
    provideClientHydration(),
    provideAnimations(),
    // SSR needs zone-based change detection; zoneless breaks initial navigation on the server
    provideZoneChangeDetection({ eventCoalescing: true }),
    { provide: UrlSerializer, useClass: I18nUrlSerializer },
    { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true },
    ApiService,
    TitleService,
    LocaleService,
    {
      provide: APP_INITIALIZER,
      useFactory: appLanguageInitializerFactory,
      deps: [TranslateService, Injector, Location, AppService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [LocaleService, AppService],
      multi: true
    },
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: {
        prefix: '/assets/i18n/',
        suffix: `.json?t=${Date.now()}`
      }
    },
    { provide: BUILD_CONFIG, useValue: build },
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {
  constructor(
    router: Router,
    icons: AppIconsLibrary,
    media: MediaQuery,
    location: Location,
    scrollRestoration: ScrollRestoration,
  ) {
      const sassVariables = {
        fontSizeVar: 16,
        breakpoints: {
          small: '0',
          medium: '768px',
          large: '1024px',
          xlarge: '1300px',
          xxlarge: '1440px',
        },
      };
      icons.addIcon(iconAlert.svgData, 'error');
      icons.addIcon(iconEyeSlash.svgData, 'eye-slash');
      icons.addIcon(iconEye.svgData, 'eye');
      icons.addIcon(iconClose.svgData, 'close');
      icons.addIcon(iconCheckmark.svgData, 'checkmark');
      icons.addIcon(iconCheckBold.svgData, 'valid');
      icons.addIcon(iconCalendar.svgData, 'calendar');
      icons.addIcon(iconArrowRight.svgData, 'arrow-right');
      icons.addIcon(iconPlus.svgData, 'plus');
      icons.addIcon(iconSearch.svgData, 'search');
      icons.addIcon(iconRadiobutton.svgData, 'radio-button');
      icons.addIcon(iconRadiobuttonMark.svgData, 'radio-mark');
      icons.addIcon(iconCheckRound.svgData, 'check-round');
      icons.addIcon(iconCheckboxEmpty.svgData, 'check-empty');
      icons.addIcon(iconCheckFrameActive.svgData, 'check-frame-active');
      icons.addIcon(iconCheckbox.svgData, 'check-active');
      icons.addIcon(iconRboxEmpty.svgData, 'check-round-empty');
      icons.addIcon(iconRboxFilled.svgData, 'check-round-active');
      icons.addIcon(iconAngleDown.svgData, 'angle-down');
      icons.addIcon(iconMoveArrows.svgData, 'move-arrows');
      RouterBehavior.setup(router, location, scrollRestoration);
      media.init({ breakpoints: sassVariables.breakpoints, baseFontsize: sassVariables.fontSizeVar });
  }
}
